import { useCallback, useMemo } from 'react';

import { UseSIPDeviceCreationReturnType } from './useSIPDeviceCreation.decl';

import { SUCCESS_FEEDBACK_MESSAGES } from '@constants/translation.constants';
import {
  CreateSipSettingMutation,
  CreateSipSettingMutationVariables,
} from '@generated/CreateSipSettingMutation';
import { SipSettingType } from '@generated/globalTypes';
import { UserFullFragment_sipSettings } from '@generated/UserFullFragment';
import { CREATE_SIP_SETTING_MUTATION } from '@graphql/mutations/CreateSipSettingMutation';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { useTranslation } from 'react-i18next';

/**
 * Hook to create a custom sip device.
 * @returns an object holding a function responsible for creating a custom sip device, custom sip settings, twilio sip settings and its state
 */
export const useSIPDeviceCreation = ({
  user,
}: UserProfileTabProps): UseSIPDeviceCreationReturnType => {
  const { t } = useTranslation();

  const [mutate, { loading }] = useGraphMutationWithFeedback<
    CreateSipSettingMutation,
    CreateSipSettingMutationVariables
  >(CREATE_SIP_SETTING_MUTATION, { type: 'snackbar' });

  const onSIPDeviceSubmit = useCallback(async () => {
    const { error } = await mutate(
      {
        variables: {
          input: {
            agentID: user.ID,
            isEnabled: true,
            type: SipSettingType.TwilioSipSetting,
          },
        },
        update(cache, { data }) {
          cache.modify({
            id: cache.identify({ __typename: 'AgentV2', ID: user.ID }),
            fields: {
              sipSettings(sipSettings) {
                const {
                  createSipSetting: { ID, ...sipSetting },
                } = data!;

                return [
                  ...(sipSettings ?? []).filter(
                    // replace the previous twilioSipSetting
                    ({ type }: UserFullFragment_sipSettings) =>
                      type === SipSettingType.CustomSipSetting
                  ),
                  {
                    ...sipSetting,
                    __typename: 'AgentSipSetting',
                    ID,
                  },
                ];
              },
            },
          });
        },
      },
      {
        successMessage: t(SUCCESS_FEEDBACK_MESSAGES.CREATION_SUCCESS, {
          name: t(
            'user_profile.call_preferences.forward_call_sip.generation_success_feedback_prefix'
          ),
        }),
      }
    );
    return error;
  }, [mutate, user.ID, t]);

  const customSipSettings = useMemo(
    () => (user.sipSettings ?? []).filter(({ type }) => type === SipSettingType.CustomSipSetting),
    [user.sipSettings]
  );

  const twilioSipSettings = useMemo(
    () => (user.sipSettings ?? []).filter(({ type }) => type === SipSettingType.TwilioSipSetting),
    [user.sipSettings]
  );

  return { onSIPDeviceSubmit, loading, customSipSettings, twilioSipSettings };
};
